import { useEffect, useMemo, useState } from 'react';

const buildFeatureFlagMap = (flags) =>
  flags.reduce((featureFlags, flag) => {
    const flagKey = flag.flagKey ?? flag.FlagKey;

    if (flagKey) {
      featureFlags[flagKey] = flag.isEnabled ?? flag.IsEnabled;
    }

    return featureFlags;
  }, {});

export default function useFeatureFlags(flagKeys, enabled = true) {
  const flagKeysSignature = Array.isArray(flagKeys) ? flagKeys.filter(Boolean).join('|') : flagKeys || '';
  const requestedFlagKeys = useMemo(() => (flagKeysSignature ? flagKeysSignature.split('|') : []), [flagKeysSignature]);
  const [featureFlags, setFeatureFlags] = useState({});
  const [isLoaded, setIsLoaded] = useState(!enabled || requestedFlagKeys.length === 0);

  useEffect(() => {
    let isMounted = true;

    const loadFeatureFlags = async () => {
      setIsLoaded(false);

      try {
        const { getFeatureFlags } = await import('src/utilities/LanternApiClient');
        const response = await getFeatureFlags(requestedFlagKeys);
        if (!isMounted) return;

        if (response?.success && Array.isArray(response.body)) {
          setFeatureFlags(buildFeatureFlagMap(response.body));
        } else {
          setFeatureFlags({});
        }
      } catch (_error) {
        if (isMounted) {
          setFeatureFlags({});
        }
      } finally {
        if (isMounted) {
          setIsLoaded(true);
        }
      }
    };

    if (enabled && requestedFlagKeys.length > 0) {
      loadFeatureFlags();
    } else {
      setFeatureFlags({});
      setIsLoaded(true);
    }

    return () => {
      isMounted = false;
    };
  }, [enabled, requestedFlagKeys]);

  return { featureFlags, isLoaded };
}
